import { unreadReviewCompletions } from "@/entities/notification/completion-alerts"
import { getAssistantConversation } from "./assistant.api"
import {
  ASSISTANT_ACCEPTANCE_MS,
  assistantRunWatchExpired,
  browserAssistantNotificationsEnabled,
  finishWatchedAssistantRequest,
  forgetWatchedAssistantRequest,
  markAssistantRequestActive,
  unreadAssistantCompletions,
  watchedAssistantRequests,
} from "./assistant.completion-notifications"

const BLUE_DOT_FAVICON = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="11" fill="#2563EB"/></svg>'
)}`
let originalFavicon: {
  element: HTMLLinkElement
  href: string | null
  type: string | null
} | null = null
const browserNotifications = new Map<string, Notification>()

export const closeAssistantConversationNotification = (
  conversationId: string
) => {
  browserNotifications.get(conversationId)?.close()
  browserNotifications.delete(conversationId)
}

export const syncAssistantBrowserNotifications = () => {
  const unread = new Set(
    unreadAssistantCompletions().map((entry) => entry.conversationId)
  )
  for (const conversationId of browserNotifications.keys()) {
    if (!unread.has(conversationId))
      closeAssistantConversationNotification(conversationId)
  }
}

const restoreFavicon = () => {
  if (!originalFavicon) return
  const { element, href, type } = originalFavicon
  if (href === null) element.removeAttribute("href")
  else element.setAttribute("href", href)
  if (type === null) element.removeAttribute("type")
  else element.setAttribute("type", type)
}

export const syncAssistantCompletionFavicon = () => {
  const icon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (!icon) return
  if (originalFavicon?.element !== icon) {
    originalFavicon = {
      element: icon,
      href: icon.getAttribute("href"),
      type: icon.getAttribute("type"),
    }
  }
  if (unreadAssistantCompletions().length || unreadReviewCompletions().length) {
    icon.setAttribute("href", BLUE_DOT_FAVICON)
    icon.setAttribute("type", "image/svg+xml")
  } else {
    restoreFavicon()
  }
}

export const clearAssistantCompletionFavicon = () => {
  restoreFavicon()
  originalFavicon = null
}

export const conversationAtPath = (pathname: string) =>
  /^\/assistant\/([^/]+)\/?$/.exec(pathname)?.[1]

export const isVisibleConversation = (conversationId: string) =>
  document.visibilityState !== "hidden" &&
  conversationAtPath(window.location.pathname) === conversationId

export const isAwayFromConversation = (conversationId: string) =>
  !isVisibleConversation(conversationId) || !document.hasFocus()

export const notifyAssistantCompletion = (
  conversationId: string,
  title: string,
  body: string,
  onClick: () => void
) => {
  if (
    !browserAssistantNotificationsEnabled() ||
    typeof Notification === "undefined" ||
    Notification.permission !== "granted" ||
    !isAwayFromConversation(conversationId)
  )
    return false
  try {
    closeAssistantConversationNotification(conversationId)
    const notification = new Notification(title, {
      body,
      tag: `assistant-${conversationId}`,
    })
    browserNotifications.set(conversationId, notification)
    notification.onclose = () => {
      if (browserNotifications.get(conversationId) === notification) {
        browserNotifications.delete(conversationId)
      }
    }
    notification.onclick = () => {
      closeAssistantConversationNotification(conversationId)
      if (
        unreadAssistantCompletions().some(
          (entry) => entry.conversationId === conversationId
        )
      ) {
        onClick()
      }
    }
    return true
  } catch {
    // The tab favicon remains the fallback when the OS refuses a browser notification.
    return false
  }
}

/** A transcript is the source of truth: only the exact persisted request ID may trigger an alert. */
export const checkWatchedAssistantCompletions = async (
  onComplete: (conversationId: string) => void
) => {
  await Promise.all(
    watchedAssistantRequests().map(async (entry) => {
      const { conversationId, requestId } = entry
      try {
        const transcript = await getAssistantConversation({ conversationId })
        if (transcript.finishedRequestIds?.includes(requestId)) {
          if (
            finishWatchedAssistantRequest(
              conversationId,
              requestId,
              isAwayFromConversation(conversationId)
            )
          ) {
            onComplete(conversationId)
          }
        } else if (transcript.activeRun?.requestId === requestId) {
          markAssistantRequestActive(requestId)
        } else if (
          !entry.seenActive &&
          Date.now() - entry.startedAt >= ASSISTANT_ACCEPTANCE_MS
        ) {
          // Retire only after a successful server check. A sleeping/offline browser may wake
          // after five minutes to find that the exact request completed while it was away.
          forgetWatchedAssistantRequest(requestId)
        } else if (assistantRunWatchExpired(entry)) {
          // A sleeping/offline tab must query the transcript before retiring a run.
          forgetWatchedAssistantRequest(requestId)
        }
      } catch (caught) {
        const status = (caught as { response?: { status?: number } })?.response
          ?.status
        if (status === 403 || status === 404)
          forgetWatchedAssistantRequest(requestId)
        // Network and auth-refresh failures may be temporary. Retry without claiming completion.
      }
    })
  )
}
